import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./KnowledgeBase.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
const TOKEN_KEY = "young-knowledge-admin-token";

const EMPTY_RECORD = {
  id: "",
  type: "profile",
  title: "",
  topics: "",
  claim: "",
  context: "",
  decision: "",
  outcome: "",
  metrics: "",
  artifact_url: "",
  visibility: "public",
};

function recordToForm(rec) {
  if (!rec) return { ...EMPTY_RECORD };
  return {
    id: rec.id || "",
    type: rec.type || "profile",
    title: rec.title || "",
    topics: (rec.topics || []).join(", "),
    claim: rec.claim || "",
    context: rec.context || "",
    decision: rec.decision || "",
    outcome: rec.outcome || "",
    metrics: (rec.metrics || []).join("\n"),
    artifact_url: rec.artifact_url || "",
    visibility: rec.visibility || "public",
  };
}

function formToRecord(form) {
  return {
    id: form.id.trim(),
    type: form.type,
    title: form.title.trim(),
    topics: form.topics
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean),
    claim: form.claim.trim(),
    context: form.context.trim(),
    decision: form.decision.trim(),
    outcome: form.outcome.trim(),
    metrics: form.metrics
      .split("\n")
      .map((m) => m.trim())
      .filter(Boolean),
    artifact_url: form.artifact_url.trim(),
    visibility: form.visibility,
  };
}

async function api(path, { token, method = "GET", body } = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Knowledge-Token": token,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

const KnowledgeBase = () => {
  const [status, setStatus] = useState(null);
  const [tokenInput, setTokenInput] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("corpus");
  const [corpus, setCorpus] = useState([]);
  const [recordTypes, setRecordTypes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_RECORD });
  const [questionsText, setQuestionsText] = useState("");
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testQuery, setTestQuery] = useState("");
  const [testHits, setTestHits] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/knowledge/status`)
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus({ enabled: false }));
  }, []);

  const load = useCallback(async (authToken) => {
    const data = await api("/api/knowledge", { token: authToken });
    setCorpus(data.corpus || []);
    setRecordTypes(data.recordTypes || []);
    setQuestionsText((data.questions || []).join("\n"));
    const first = data.corpus?.[0]?.id;
    if (first) {
      setSelectedId(first);
      setForm(recordToForm(data.corpus.find((r) => r.id === first)));
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    load(token).catch((err) => {
      setMessage({ type: "err", text: err.message });
      sessionStorage.removeItem(TOKEN_KEY);
      setToken("");
    });
  }, [token, load]);

  const sortedCorpus = useMemo(
    () => [...corpus].sort((a, b) => a.title.localeCompare(b.title)),
    [corpus]
  );

  const selectRecord = (id) => {
    setSelectedId(id);
    const rec = corpus.find((r) => r.id === id);
    setForm(recordToForm(rec));
    setMessage(null);
  };

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const unlock = () => {
    const t = tokenInput.trim();
    if (!t) return;
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setMessage(null);
  };

  const saveRecord = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const record = formToRecord(form);
      await api("/api/knowledge/corpus/record", { token, method: "POST", body: record });
      await load(token);
      setSelectedId(record.id);
      setMessage({ type: "ok", text: `Saved “${record.id}”. Agent uses this immediately.` });
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const newRecord = () => {
    setSelectedId(null);
    setForm({ ...EMPTY_RECORD, id: `new-record-${Date.now().toString(36)}` });
    setMessage(null);
  };

  const removeRecord = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Delete record “${selectedId}”?`)) return;
    setBusy(true);
    try {
      await api(`/api/knowledge/corpus/${encodeURIComponent(selectedId)}`, {
        token,
        method: "DELETE",
      });
      await load(token);
      setMessage({ type: "ok", text: "Record deleted." });
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const saveQuestions = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const questions = questionsText
        .split("\n")
        .map((q) => q.trim())
        .filter(Boolean);
      await api("/api/knowledge/questions", { token, method: "PUT", body: { questions } });
      setMessage({ type: "ok", text: `Saved ${questions.length} starter questions.` });
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const runTest = async () => {
    setBusy(true);
    setTestHits(null);
    try {
      const data = await api("/api/knowledge/test-retrieve", {
        token,
        method: "POST",
        body: { text: testQuery },
      });
      setTestHits(data.evidence || []);
    } catch (err) {
      setMessage({ type: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="kb-page">
        <div className="kb-wrap">Loading…</div>
      </div>
    );
  }

  return (
    <div className="kb-page">
      <div className="kb-wrap">
        <header className="kb-head">
          <div>
            <h1>Knowledge base</h1>
            <p>
              Corpus for Digital Young. Rules and quick fixes live on <Link to="/admin">/admin</Link>.
            </p>
          </div>
          <div className="kb-head__links">
            <Link to="/admin" className="kb-btn kb-btn--ghost">
              Gallery admin
            </Link>
            <Link to="/agent" className="kb-btn kb-btn--ghost">
              Open agent →
            </Link>
          </div>
        </header>

        {!status.enabled && (
          <div className="kb-gate kb-msg kb-msg--err">
            Add <code>KNOWLEDGE_ADMIN_TOKEN=your-secret</code> to <code>server/.env</code>, restart the API on
            :3001, then reload this page.
          </div>
        )}

        {status.enabled && !token && (
          <div className="kb-gate">
            <label htmlFor="kb-token">Admin token</label>
            <input
              id="kb-token"
              type="password"
              autoComplete="off"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              placeholder="Same value as KNOWLEDGE_ADMIN_TOKEN"
            />
            <button type="button" className="kb-btn" onClick={unlock}>
              Unlock
            </button>
          </div>
        )}

        {token && (
          <>
            <div className="kb-tabs">
              <button type="button" className={tab === "corpus" ? "is-active" : ""} onClick={() => setTab("corpus")}>
                Corpus ({corpus.length})
              </button>
              <button
                type="button"
                className={tab === "questions" ? "is-active" : ""}
                onClick={() => setTab("questions")}
              >
                Starter questions
              </button>
              <button
                type="button"
                className="kb-btn kb-btn--ghost"
                onClick={() => {
                  sessionStorage.removeItem(TOKEN_KEY);
                  setToken("");
                }}
              >
                Lock
              </button>
            </div>

            {tab === "corpus" && (
              <div className="kb-layout">
                <div className="kb-list">
                  <button type="button" onClick={newRecord}>
                    + New record
                  </button>
                  {sortedCorpus.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className={selectedId === r.id ? "is-active" : ""}
                      onClick={() => selectRecord(r.id)}
                    >
                      {r.title}
                      <small>{r.id}</small>
                    </button>
                  ))}
                </div>

                <div className="kb-editor">
                  <div className="kb-field">
                    <label htmlFor="kb-id">Id (kebab-case)</label>
                    <input id="kb-id" value={form.id} onChange={(e) => updateField("id", e.target.value)} />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-type">Type</label>
                    <select id="kb-type" value={form.type} onChange={(e) => updateField("type", e.target.value)}>
                      {recordTypes.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-title">Title</label>
                    <input id="kb-title" value={form.title} onChange={(e) => updateField("title", e.target.value)} />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-topics">Topics (comma-separated, used for search)</label>
                    <input
                      id="kb-topics"
                      value={form.topics}
                      onChange={(e) => updateField("topics", e.target.value)}
                      placeholder="collabstr, design system, sop"
                    />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-claim">Claim (main fact)</label>
                    <textarea id="kb-claim" value={form.claim} onChange={(e) => updateField("claim", e.target.value)} />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-context">Context</label>
                    <textarea id="kb-context" value={form.context} onChange={(e) => updateField("context", e.target.value)} />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-decision">Decision</label>
                    <textarea id="kb-decision" value={form.decision} onChange={(e) => updateField("decision", e.target.value)} />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-outcome">Outcome</label>
                    <textarea id="kb-outcome" value={form.outcome} onChange={(e) => updateField("outcome", e.target.value)} />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-metrics">Metrics (one per line)</label>
                    <textarea id="kb-metrics" value={form.metrics} onChange={(e) => updateField("metrics", e.target.value)} />
                  </div>
                  <div className="kb-field">
                    <label htmlFor="kb-url">Artifact URL</label>
                    <input
                      id="kb-url"
                      value={form.artifact_url}
                      onChange={(e) => updateField("artifact_url", e.target.value)}
                      placeholder="/Collabstr or https://…"
                    />
                  </div>

                  <div className="kb-actions">
                    <button type="button" className="kb-btn" disabled={busy} onClick={saveRecord}>
                      Save record
                    </button>
                    {selectedId && (
                      <button type="button" className="kb-btn kb-btn--danger" disabled={busy} onClick={removeRecord}>
                        Delete
                      </button>
                    )}
                  </div>

                  <div className="kb-test">
                    <div className="kb-field">
                      <label htmlFor="kb-test">Test retrieval (what the agent would see)</label>
                      <input
                        id="kb-test"
                        value={testQuery}
                        onChange={(e) => setTestQuery(e.target.value)}
                        placeholder="What's your design philosophy?"
                      />
                    </div>
                    <button type="button" className="kb-btn kb-btn--ghost" disabled={busy || !testQuery.trim()} onClick={runTest}>
                      Run test
                    </button>
                    {testHits && (
                      <ul>
                        {testHits.length === 0 && <li>No matches: add topics or keywords.</li>}
                        {testHits.map((h) => (
                          <li key={h.id}>
                            <strong>{h.title}</strong>: {h.claim}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "questions" && (
              <div className="kb-panel kb-questions">
                <p style={{ marginTop: 0, color: "#666" }}>
                  One question per line. Shown as chips on <code>/agent</code> when the bot has no follow-ups.
                </p>
                <textarea value={questionsText} onChange={(e) => setQuestionsText(e.target.value)} />
                <div className="kb-actions">
                  <button type="button" className="kb-btn" disabled={busy} onClick={saveQuestions}>
                    Save questions
                  </button>
                </div>
              </div>
            )}

            {message && <div className={`kb-msg kb-msg--${message.type}`}>{message.text}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default KnowledgeBase;
